import { useState } from 'react';
import { CheckCircle, MessageCircle, Phone, MapPin, Mail } from 'lucide-react';
import { useScrollReveal } from '../../hooks/useScrollReveal';

const WHATSAPP_NUMBER = '5575981872440';

const consultBenefits = [
  'Avaliação clínica completa dos seus exames',
  'Discussão das características do seu nódulo',
  'Explicação clara do nível de risco e próximos passos',
  'Análise de cobertura de convênio',
  'Resposta a todas as suas dúvidas',
  'Plano de ação personalizado para o seu caso',
];

export default function NoduloContact() {
  const [ref, isVisible] = useScrollReveal();
  const [form, setForm] = useState({ name: '', phone: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    const parts = [
      'Olá, vim do site de nódulo pulmonar e gostaria de agendar uma consulta de avaliação com dr. Mateus.',
      `Meu nome é ${form.name} e meu telefone é ${form.phone}.`,
    ];
    if (form.message) parts.push(form.message);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(parts.join(' '))}`, '_blank');
    setSubmitted(true);
  };

  return (
    <section
      id="contato"
      ref={ref}
      className={`py-24 md:py-32 bg-gradient-to-br from-[#0D47A1] to-[#00897B] transition-all duration-700 ease-out ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 lg:px-8">

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

          <div className="text-white">
            <div className="inline-flex items-center bg-white/15 border border-white/25 rounded-full px-4 py-1.5 text-sm font-semibold mb-6">
              Agende Sua Avaliação
            </div>
            <h2 className="font-poppins font-bold text-3xl sm:text-4xl mb-6 leading-tight">
              Marque Sua Avaliação Agora
            </h2>
            <p className="text-white/85 text-base md:text-lg leading-relaxed mb-8">
              Você descobriu um nódulo e está aqui buscando respostas. Isso significa que você é proativo
              e responsável com sua saúde. Não será rápido e superficial. Será conversa real, com tempo
              dedicado, com explicações claras. Você sai da consulta sabendo exatamente o que precisa ser feito.
            </p>

            <div className="space-y-3 mb-8">
              {consultBenefits.map((benefit, i) => (
                <div key={i} className="flex items-center gap-3">
                  <CheckCircle size={18} className="text-white/90 flex-shrink-0" />
                  <span className="text-white/85 text-sm md:text-base">{benefit}</span>
                </div>
              ))}
            </div>

            <div className="space-y-3 mb-8">
              <div className="flex items-center gap-3">
                <MapPin size={16} className="text-white/70 flex-shrink-0" />
                <span className="text-white/75 text-sm">Feira de Santana - BA</span>
              </div>
              <div className="flex items-center gap-3">
                <Phone size={16} className="text-white/70 flex-shrink-0" />
                <a href="tel:+5575981872440" className="text-white/75 text-sm hover:text-white transition-colors">
                  (75) 98187-2440
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Mail size={16} className="text-white/70 flex-shrink-0" />
                <a href="mailto:mateus.cirtorax@gmail.com" className="text-white/75 text-sm hover:text-white transition-colors">
                  mateus.cirtorax@gmail.com
                </a>
              </div>
            </div>

            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('Olá, vim do site de nódulo e gostaria de agendar uma consulta com dr. Mateus')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 bg-white text-[#0D47A1] font-poppins font-bold px-8 py-4 rounded-full hover:bg-gray-50 transition-all duration-200 shadow-lg hover:-translate-y-0.5 max-w-xs"
            >
              <Phone size={18} />
              AGENDAR CONSULTA INICIAL
            </a>
          </div>

          <div className="bg-white rounded-3xl p-8 shadow-2xl">
            {submitted ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <div className="w-16 h-16 bg-[#00897B]/10 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle size={32} className="text-[#00897B]" />
                </div>
                <h3 className="font-poppins font-bold text-xl text-[#212121] mb-2">Obrigado!</h3>
                <p className="text-[#212121]/60 text-sm leading-relaxed">
                  Sua mensagem foi enviada pelo WhatsApp.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="mt-6 text-[#0D47A1] text-sm font-semibold hover:underline"
                >
                  Enviar nova mensagem
                </button>
              </div>
            ) : (
              <>
                <h3 className="font-poppins font-bold text-xl text-[#212121] mb-2">
                  Envie sua Mensagem
                </h3>
                <p className="text-[#212121]/60 text-sm mb-6">
                  Preencha e falaremos com você pelo WhatsApp em breve.
                </p>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-[#212121] mb-1.5">
                      Seu nome *
                    </label>
                    <input
                      type="text"
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Como prefere ser chamado(a)?"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-[#212121] placeholder-gray-400 focus:outline-none focus:border-[#0D47A1] focus:ring-2 focus:ring-[#0D47A1]/10 transition-all text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#212121] mb-1.5">
                      Telefone / WhatsApp *
                    </label>
                    <input
                      type="tel"
                      required
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="(00) 00000-0000"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-[#212121] placeholder-gray-400 focus:outline-none focus:border-[#0D47A1] focus:ring-2 focus:ring-[#0D47A1]/10 transition-all text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#212121] mb-1.5">
                      Sua dúvida ou mensagem (opcional)
                    </label>
                    <textarea
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      placeholder="Conte um pouco sobre sua situação..."
                      rows={3}
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-[#212121] placeholder-gray-400 focus:outline-none focus:border-[#0D47A1] focus:ring-2 focus:ring-[#0D47A1]/10 transition-all text-sm resize-none"
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full flex items-center justify-center gap-2 bg-[#0D47A1] text-white font-poppins font-bold py-4 rounded-xl hover:bg-[#00897B] transition-colors duration-200 shadow-lg hover:-translate-y-0.5 hover:shadow-xl"
                  >
                    <MessageCircle size={18} />
                    ENVIAR PELO WHATSAPP
                  </button>
                </form>
                <p className="text-[#212121]/45 text-xs text-center mt-4">
                  Seus dados são usados apenas para agendamento. Nunca serão compartilhados.
                </p>
              </>
            )}
          </div>

        </div>
      </div>
    </section>
  );
}
